import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import {
    AffairesDataServiceName,
    AffairesEndpoints,
    IAffairesDataService,
} from '../../../DataServices/AffairesDataService';
import { Lazy } from '../../../Helpers/Lazy';
import {
    AddOrEditAffaireDetails,
    AffaireControlApiDataResponse,
    AffaireDetails,
    AffaireDetailsApiDataResponse,
    AffaireListOrder,
    AffaireListResponse,
} from '../../../Models/Affaires/Affaires';
import {
    Element,
    ElementListResponse,
    UsageByWeekResponse,
} from '../../../Models/Affaires/Elements';
import { BaseApiDataResponse } from '../../../Models/ApiData';
import { ExportFileType, ExportUtils } from '../../../Utils/ExportUtils';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { AuthServiceName, IAuthService } from '../Authentication/AuthenticationService';
import { DownloadServiceName, IDownloadService } from '../Download/DownloadService';
import { I18nServiceName, II18nService } from '../I18n/I18nService';

export interface IAffairesService {
    getAffaireList: (
        cts: XHRRequestCanceler,
        search: string,
        isActive: boolean,
        order?: AffaireListOrder
    ) => Promise<AffaireListResponse>;

    setAffaireState: (
        cts: XHRRequestCanceler,
        affaireId: string,
        state: boolean
    ) => Promise<BaseApiDataResponse>;

    getAffaireControl: (
        cts: XHRRequestCanceler,
        startDate: Date,
        endDate: Date,
        affaireId: string
    ) => Promise<AffaireControlApiDataResponse>;

    getAffaireDetails: (
        affaireId: string,
        cts: XHRRequestCanceler
    ) => Promise<AffaireDetailsApiDataResponse>;

    patchAffaire: (
        affaireId: string,
        affaireInfos: AddOrEditAffaireDetails,
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    createAffaire: (
        affaireInfos: AddOrEditAffaireDetails,
        cts: XHRRequestCanceler
    ) => Promise<AffaireDetailsApiDataResponse>;

    exportAffaireList: (
        cts: XHRRequestCanceler,
        search: string,
        order?: AffaireListOrder,
        isActive?: boolean | undefined
    ) => void;

    listElements: (cts: XHRRequestCanceler, affaireId: string) => Promise<ElementListResponse>;

    setElements: (
        cts: XHRRequestCanceler,
        affaireId: string,
        elements: Element[]
    ) => Promise<BaseApiDataResponse>;

    createElement: (
        cts: XHRRequestCanceler,
        affaireId: string,
        element: Element
    ) => Promise<BaseApiDataResponse>;

    getUsageByWeek: (cts: XHRRequestCanceler, element: Element) => Promise<UsageByWeekResponse>;
}

class AffairesService extends BaseReduxService implements IAffairesService {
    private readonly affairesDataService: Lazy<IAffairesDataService>;
    private readonly i18nService: Lazy<II18nService>;
    private readonly authService: Lazy<IAuthService>;
    private readonly downloadService: Lazy<IDownloadService>;

    constructor() {
        super();
        this.affairesDataService = IoC.getLazy<IAffairesDataService>(AffairesDataServiceName);
        this.i18nService = IoC.getLazy<II18nService>(I18nServiceName);
        this.authService = IoC.getLazy<IAuthService>(AuthServiceName);
        this.downloadService = IoC.getLazy<IDownloadService>(DownloadServiceName);
    }

    public getUsageByWeek = async (
        cts: XHRRequestCanceler,
        element: Element
    ): Promise<UsageByWeekResponse> => {
        try {
            const response = await this.affairesDataService.value().getUsageByWeek(cts, element);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public createElement = async (
        cts: XHRRequestCanceler,
        affaireId: string,
        element: Element
    ): Promise<BaseApiDataResponse> => {
        try {
            const response = await this.affairesDataService
                .value()
                .createElement(cts, affaireId, element);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public setElements = async (
        cts: XHRRequestCanceler,
        affaireId: string,
        elements: Element[]
    ): Promise<BaseApiDataResponse> => {
        try {
            const response = await this.affairesDataService
                .value()
                .setElements(cts, affaireId, elements);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public listElements = async (
        cts: XHRRequestCanceler,
        affaireId: string
    ): Promise<ElementListResponse> => {
        try {
            const response = await this.affairesDataService.value().listElements(cts, affaireId);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getAffaireList = async (
        cts: XHRRequestCanceler,
        search: string,
        isActive: boolean,
        order?: AffaireListOrder
    ) => {
        try {
            const response = await this.affairesDataService
                .value()
                .getAffaireList(cts, search, isActive, order);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public setAffaireState = async (cts: XHRRequestCanceler, affaireId: string, state: boolean) => {
        try {
            const response = await this.affairesDataService
                .value()
                .setAffaireState(cts, affaireId, state);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getAffaireControl = async (
        cts: XHRRequestCanceler,
        startDate: Date,
        endDate: Date,
        affaireId: string
    ) => {
        try {
            const response = await this.affairesDataService.value().getAffaireControl(
                {
                    affaire_id: affaireId,
                    from_day_date: `${startDate.getFullYear()}-${
                        startDate.getMonth() + 1
                    }-${startDate.getDate()}`,
                    to_day_date: `${endDate.getFullYear()}-${
                        endDate.getMonth() + 1
                    }-${endDate.getDate()}`,
                },
                cts
            );
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getAffaireDetails = async (affaireId: string, cts: XHRRequestCanceler) => {
        try {
            const response = this.affairesDataService.value().getAffaireDetails(affaireId, cts);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public patchAffaire = async (
        affaireId: string,
        affaireInfos: AddOrEditAffaireDetails,
        cts: XHRRequestCanceler
    ) => {
        try {
            const response = await this.affairesDataService
                .value()
                .patchAffaire(affaireId, affaireInfos, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
                return Promise.reject(response.error_message);
            }
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public createAffaire = async (
        affaireInfos: AddOrEditAffaireDetails,
        cts: XHRRequestCanceler
    ) => {
        try {
            const response = await this.affairesDataService
                .value()
                .createAffaire(affaireInfos, cts);

            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
                return Promise.reject(response.error_message);
            }
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public exportAffaireList = async (
        cts: XHRRequestCanceler,
        search: string,
        order?: AffaireListOrder | undefined,
        isActive?: boolean | undefined
    ): Promise<void> => {
        try {
            const params: { [key: string]: string } = {};
            params['search'] = search;
            if (order) {
                params['order'] = order.valueOf();
            }
            if (isActive === true) {
                params['is_active'] = isActive.valueOf().toString();
            }
            const url: string = ExportUtils.getExportUrlWithParams(
                {
                    url: AffairesEndpoints.EXPORT_AFFAIRE_LIST,
                    auth: await this.authService.value().getAuthorization(),
                },
                params
            );
            this.downloadService.value().downloadFile(url, 'Chantiers', ExportFileType.EXCEL, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };
}

const AffairesServiceName = 'AffairesService';
export { AffairesService, AffairesServiceName };
